/**
 * ASNLoadsComparison
 *
 * "ASN Loads vs Non-ASN Loads" horizontal 100%-stacked-bar chart for the ASN
 * Gap Overview tab, backed by `POST /asn/loads-vs-non-asn/chart`. One
 * bar per row at the current Region / Market / Location hierarchy level.
 *
 * DRILL-DOWN: clicking a Region or Market bar drills into the next level
 * scoped to that lineage (never navigates to another tab); Location bars
 * are the final level and are not pressable. All three levels come from the
 * single flat API response already fetched by `AsnGapDashboard` — drilling
 * only changes which rows are visible (`getVisibleLoadsVsNonAsnItems`), it
 * never triggers another request. A "Back" link steps back up one level at
 * a time (Location -> Market -> Region), keeping Chart Configuration in
 * sync at every step.
 *
 * This is a new component rather than a reuse/extension of
 * `PTExecutionSection`'s breakdown bars. That component renders a SINGLE
 * percentage segment per row (0–100% of one metric) with a fixed color rule
 * already resolved upstream. This chart needs a fundamentally different bar
 * geometry: TWO segments per row (Non-ASN + ASN) that always sum to 100% of
 * total loads, a fixed-position target tick mark independent of row values,
 * and Non-ASN segment color/label color driven by the backend's own
 * per-row `isAboveTarget` evaluation. Forcing reuse would have required
 * significant conditional rendering inside PT Execution and risked
 * regressing its existing tests/output, which the task explicitly guards
 * against. Only safe, generic presentation primitives are shared: the same
 * card/typography/shadow tokens (`asnOverviewChartsStyles`, modeled on
 * `PTExecutionSectionStyles`) and the same `ChartSkeleton` loading component.
 */

import React, { useMemo } from 'react';
import { AccessibilityRole, Platform, Pressable, Text, View, useWindowDimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { DLOSimpleTooltip } from '../../../../../modules/dlo/components/DLOTooltip';
import { asnOverviewChartsStyles as styles } from '../styles/asnOverviewChartsStyles';
import ChartSkeleton from '../../../components/ChartSkeleton';
import DLODropdown from '../../../../../modules/dlo/components/DLODropdown';
import { DLODropdownOption } from '../../../../../modules/dlo/types/dlo.types';
import { LoadsVsNonAsnChartSortBy } from '../api/asnOverviewKpiApi';
import { AsnLoadsChartItem, LOADS_VS_NON_ASN_SORT_OPTIONS, formatCount, formatPercent1 } from '../utils/asnLoadsComparisonAdapter';
import { OverviewHierarchyLevel } from './Overview';
import { FontFamily } from '../../../../../constants/fonts';

const SEARCH_DEBOUNCE_MS = 350;

// Memoized style object for DLODropdown placeholder — defined outside component
// to prevent recreation on every render, which could trigger infinite re-render cycles
const DLO_PLACEHOLDER_STYLE = {
  color: '#8A8F98',
  fontSize: 13,
  fontFamily: FontFamily.regular,
  fontWeight: '400' as const,
};

export interface ASNLoadsComparisonProps {
  /** Current Chart Configuration → Location Hierarchy level — every row in
   * `rows` already belongs to this level (see `getVisibleLoadsVsNonAsnItems`). */
  hierarchyLevel: OverviewHierarchyLevel;
  hierarchyLabel?: string;
  rows: AsnLoadsChartItem[];
  /** Optional PBNA-Total summary bar, derived from the KPI response grand-total
   * values. When provided, rendered below a horizontal divider after all hierarchy
   * bars. Not included in sorting, drill-down or row-click behaviour. */
  grandTotal?: AsnLoadsChartItem;
  /** True once the user has drilled below Region — shows the Back link. */
  canGoBack?: boolean;
  onBack?: () => void;
  onNavigateToLevel?: (level: OverviewHierarchyLevel, region: string | null, market: string | null) => void;
  drillRegion?: string | null;
  drillMarket?: string | null;
  /** Fired when a Region or Market bar is pressed (never for Location bars,
   * which are the final level and are not pressable). */
  onBarPress?: (item: AsnLoadsChartItem) => void;
  sortBy?: LoadsVsNonAsnChartSortBy;
  onSortChange?: (sortBy: LoadsVsNonAsnChartSortBy) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Backend-provided copy shown when `rows` is empty (e.g. no matches for
   * the current search/filters) — rendered verbatim, never replaced with a
   * hardcoded string. */
  message?: string;
}

const MOBILE_BREAKPOINT = 620;
const TABLET_BREAKPOINT = 900;

/**
 * Threshold below which the raw count inside a bar segment would be clipped
 * or unreadably cramped — below this we omit the inside label (the value
 * remains available via the accessibility label / row tooltip text).
 */
const MIN_SEGMENT_PCT_FOR_LABEL = 12;

const SORT_DROPDOWN_ITEMS: DLODropdownOption[] = LOADS_VS_NON_ASN_SORT_OPTIONS.map((opt) => ({
  id: opt.key,
  label: opt.label,
  value: opt.key,
}));

const BarRow: React.FC<{
  item: AsnLoadsChartItem;
  targetPercentage: number;
  layout: 'desktop' | 'tablet' | 'mobile';
  isDrillable: boolean;
  // Named `onItemPress` (not `onPress`) deliberately: `@testing-library/react-native`'s
  // `fireEvent.press` walks up the *composite* element tree (not just host
  // elements) looking for any ancestor whose props contain an `onPress`-named
  // function, and invokes it directly if the pressed host element itself has
  // none. Since this prop is only conditionally forwarded to a real
  // `Pressable` below (never wired up at all for non-drillable rows), calling
  // it `onPress` here would make `fireEvent.press` on the plain, non-Pressable
  // `View` incorrectly bubble up and invoke it anyway.
  onItemPress?: (item: AsnLoadsChartItem) => void;
  /** Extra styles merged onto the outer row container (e.g. for the total row divider). */
  overrideRowStyle?: object[];
  /** Extra styles merged onto the label Text (e.g. bold for the total row). */
  overrideLabelStyle?: object[];
}> = ({ item, targetPercentage, layout, isDrillable, onItemPress, overrideRowStyle, overrideLabelStyle }) => {
  const overTarget = item.isAboveTarget;
  const nonAsnWidthPct = Math.max(0, Math.min(100, item.nonASNPercentage));
  const showInsideLabel = nonAsnWidthPct >= MIN_SEGMENT_PCT_FOR_LABEL;
  const isMobile = layout === 'mobile';
  const isTablet = layout === 'tablet';

  const accessibilityLabel = `${item.label}: ${formatCount(item.nonASNLoads)} non-ASN loads of ${formatCount(
    item.totalLoads,
  )} total loads, ${formatPercent1(item.nonASNPercentage)} non-ASN, ${
    overTarget ? 'above' : 'at or below'
  } the ${formatPercent1(item.targetPercentage)} target.`;

  const content = (
    <>
      <Text
        style={[styles.barRowLabel, isTablet && styles.barRowLabelTablet, isMobile && styles.barRowLabelMobile, ...(overrideLabelStyle ?? [])]}
        numberOfLines={1}
      >
        {item.label}
      </Text>

      <View style={styles.barTrackWrap}>
        <View style={styles.barTrack}>
          <View
            style={[
              styles.barSegmentNonAsn,
              overTarget ? styles.barSegmentNonAsnRed : styles.barSegmentNonAsnBlue,
              { width: `${nonAsnWidthPct}%` },
            ]}
          >
            {showInsideLabel && (
              <Text style={styles.barSegmentLabel} numberOfLines={1}>
                {formatCount(item.nonASNLoads)}
              </Text>
            )}
          </View>
          <View style={styles.barSegmentAsn}>
            <Text style={[styles.barSegmentLabel, styles.barSegmentLabelDark]} numberOfLines={1}>
              {formatCount(item.asnLoads)}
            </Text>
          </View>
        </View>

        {!showInsideLabel && nonAsnWidthPct > 0 && (
          <Text
            style={[styles.barOutsideLabel, overTarget ? styles.pctRed : styles.pctBlue, { left: `${nonAsnWidthPct}%` }]}
            numberOfLines={1}
          >
            {formatCount(item.nonASNLoads)}
          </Text>
        )}

        {/* Fixed-position target marker — always at targetPercentage%, regardless of row values */}
        <View style={[styles.barTargetMarker, { left: `${Math.max(0, Math.min(100, targetPercentage))}%` }]} />
      </View>

      <Text style={[styles.barRowPct, isMobile && styles.barRowPctMobile, overTarget ? styles.pctRed : styles.pctBlue]} numberOfLines={1}>
        {formatPercent1(item.nonASNPercentage)}
      </Text>
    </>
  );

  const rowStyle = [styles.barRow, isMobile && styles.barRowMobile, ...(overrideRowStyle ?? [])];

  if (!isDrillable) {
    // No pointer cursor when drill-down is unavailable (plain, non-interactive row).
    return (
      <View style={[...rowStyle, { cursor: 'default' } as any]} testID={`asn-loads-comparison-row-${item.id}`}>
        {content}
      </View>
    );
  }

  return (
    <Pressable
      onPress={() => onItemPress?.(item)}
      style={({ pressed }) => [...rowStyle, { cursor: 'pointer' } as any, pressed && { opacity: 0.7 }]}
      accessibilityRole={'button' as AccessibilityRole}
      accessibilityLabel={`${accessibilityLabel} Activate to drill down.`}
      testID={`asn-loads-comparison-row-${item.id}`}
    >
      {content}
    </Pressable>
  );
};

const ASNLoadsComparison: React.FC<ASNLoadsComparisonProps> = ({
  hierarchyLevel,
  hierarchyLabel = 'Region / Unit',
  rows,
  grandTotal,
  canGoBack = false,
  onBack,
  onNavigateToLevel,
  drillRegion = null,
  drillMarket = null,
  onBarPress,
  sortBy = '',
  onSortChange,
  searchQuery = '',
  onSearchChange,
  loading = false,
  error = null,
  onRetry,
  message,
}) => {
  const { width } = useWindowDimensions();
  const layout = width < MOBILE_BREAKPOINT ? 'mobile' : width < TABLET_BREAKPOINT ? 'tablet' : 'desktop';
  const isDrillable = hierarchyLevel !== 'location';

  const selectedSortItem = useMemo(
    () => SORT_DROPDOWN_ITEMS.filter((opt) => opt.id === sortBy),
    [sortBy],
  );

  const targetPercentage = rows.length > 0 ? rows[0].targetPercentage : 25;

  const isMobileLayout = layout === 'mobile';
  const isWebLayout = !isMobileLayout && Platform.OS === 'web';

  // Build drill context suffix for the web title, e.g. "in Unit 2" or "in North East".
  // Only shown when actually drilled — at Region level there is no parent context.
  const drillContextSuffix: string | null = isWebLayout
    ? hierarchyLevel === 'market' && drillRegion
      ? drillRegion
      : hierarchyLevel === 'location' && (drillMarket ?? drillRegion)
        ? (drillMarket ?? drillRegion)
        : null
    : null;

  return (
    <View style={[styles.card, styles.fullWidthCard, styles.stretchCard]} testID="asn-loads-comparison-card">
      <View style={styles.webCardHeaderSection}>
        {/* ── Web header: title + optional inline Back button ── */}
        {isWebLayout ? (
          <View style={styles.webTitleRow}>
            <View style={styles.webTitleGroup}>
              <Text style={styles.title} numberOfLines={2}>
                {`ASN Loads vs Non-ASN Loads `}
                <Text style={styles.subTitle}>{`by ${hierarchyLabel}`}</Text>
                {drillContextSuffix ? (
                  <Text style={styles.subTitle}>{`in ${drillContextSuffix}`}</Text>
                ) : null}
              </Text>
            </View>
            {canGoBack && (
              <Pressable
                onPress={onBack}
                style={styles.webBackButton}
                accessibilityRole="button"
                accessibilityLabel="Back"
                testID="asn-loads-comparison-back"
              >
                <MaterialIcons name="arrow-back" size={13} color="#0B5FC6" />
                <Text style={styles.webBackButtonText}>Back</Text>
              </Pressable>
            )}
          </View>
        ) : (
          /* ── Mobile header: title on first line; subtitle + info icon inline on second; Back pinned top-right ── */
          <View style={styles.mobileHeaderRow}>
            <View style={styles.mobileHeaderLeft}>
              <Text style={styles.title} numberOfLines={1}>ASN Loads vs Non-ASN Loads</Text>
              <View style={styles.mobileSubtitleRow}>
                <Text style={styles.subTitle} numberOfLines={1}>{`by ${hierarchyLabel}`}</Text>
                <DLOSimpleTooltip
                  text="Each bar represents 100% of total loads. Non-ASN loads appear from the left—blue when at target and red when above target—while ASN loads appear in grey. The marker shows the 25% target. Tap a Region or Market bar to drill down."
                  position="top"
                  trigger="click"
                >
                  <View style={styles.infoIconWrap}>
                    <MaterialIcons name="info-outline" size={16} color="#8A8F98" />
                  </View>
                </DLOSimpleTooltip>
              </View>
            </View>
            {canGoBack && (
              <Pressable
                onPress={onBack}
                style={styles.mobileBackButton}
                accessibilityRole="button"
                accessibilityLabel="Back"
                testID="asn-loads-comparison-back-mobile"
              >
                <MaterialIcons name="arrow-back" size={15} color="#0B5FC6" />
                <Text style={styles.mobileBackButtonText}>Back</Text>
              </Pressable>
            )}
          </View>
        )}
        {!isMobileLayout && (
          <View style={styles.descriptionSortRow}>
            <Text style={[styles.description, styles.descriptionFlex]}>
              {`Each bar is 100% of total loads — Non-ASN (dark blue at target, red above) from the left, ASN (grey) on the right. The tick marks ${formatPercent1(
                targetPercentage,
              )}. Click a Region or Market bar to drill down.`}
            </Text>
            <View style={styles.sortDropdownWrap}>
              <DLODropdown
                label="Sort by"
                options={SORT_DROPDOWN_ITEMS}
                value={selectedSortItem.length > 0 ? selectedSortItem[0] : null}
                onSelect={(selected) => {
                  // DLODropdown returns single item in single-select mode
                  if (selected && !Array.isArray(selected)) {
                    onSortChange?.(selected.id as LoadsVsNonAsnChartSortBy);
                  }
                }}
                placeholder="Sort by..."
                selection="single"
                testID="asn-loads-comparison-sort"
                showLabel={false}
                size="small"
                style={styles.dloDropdownContainer}
                placeholderStyle={DLO_PLACEHOLDER_STYLE}
              />
            </View>
          </View>
        )}
      </View>

      <View style={styles.legendRow}>
        <View style={styles.legendItem}>
          <View style={[styles.legendSwatch, styles.barSegmentNonAsnBlue]} />
          <Text style={styles.legendText}>Non-ASN (at target)</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendSwatch, styles.barSegmentNonAsnRed]} />
          <Text style={styles.legendText}>Non-ASN (above target)</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendSwatch, styles.barSegmentAsnGray]} />
          <Text style={styles.legendText}>ASN Loads</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={styles.legendMarker} />
          <Text style={styles.legendText}>{`${formatPercent1(targetPercentage)} Target`}</Text>
        </View>
      </View>

      {loading ? (
        <ChartSkeleton height={260} barMode />
      ) : error ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.errorText}>{error}</Text>
          {onRetry && (
            <Pressable style={styles.retryButton} onPress={onRetry} accessibilityRole="button" accessibilityLabel="Retry">
              <Text style={styles.retryButtonText}>Retry</Text>
            </Pressable>
          )}
        </View>
      ) : rows.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>{message || 'No load data available for the selected filters'}</Text>
        </View>
      ) : (
        <View style={styles.barList}>
          {rows.map((item) => (
            <BarRow
              key={item.id}
              item={item}
              targetPercentage={targetPercentage}
              layout={layout}
              isDrillable={isDrillable}
              onItemPress={onBarPress}
            />
          ))}
          {grandTotal && (
            <BarRow
              key="pbna-total"
              item={{ ...grandTotal, label: 'PBNA Total' }}
              targetPercentage={targetPercentage}
              layout={layout}
              isDrillable={false}
              overrideLabelStyle={[styles.barRowLabelTotal]}
              overrideRowStyle={[styles.barRowTotal]}
            />
          )}
        </View>
      )}
    </View>
  );
};

export default React.memo(ASNLoadsComparison);

            